// #genai Phase 2 shared "Add Payment Method" button. Pixel-matched to Figma:
//   Button             : 269:29310 (desktop) / 269:29328 (mobile) — bg #007CAD + down chevron.
//   Desktop dropdown   : 630:31658 / 630:31801
//   Mobile bottom sheet: 305:26485 / 305:26528
// Clicking the button opens a payment-type menu offering "Bank (preferred)" and "Credit Card",
// which route to the dedicated add-ACH / add-card pages. Used by both the billing empty state
// and the saved-payment-methods list so the affordance stays identical.
import React, { useCallback, useRef, useState } from 'lib/react';
import {
  EVButton,
  EVTypography,
  EVPopper,
  EVClickAwayListener,
  EVPaper,
  EVDrawer,
  EVKeyboardArrowUp,
  EVKeyboardArrowDown,
  EVAccountBalance,
} from 'lib/components';
import { useTranslation } from 'lib/translation';
import PropTypes from 'prop-types';
import useMediaQuery from 'hooks/useMediaQuery';
import {
  BILLING_INFORMATION_ADD_CARD_ROUTE,
  BILLING_INFORMATION_ADD_ACH_ROUTE,
} from 'constants/RouteConstants';
import history from 'store/CreateHistory';
import AddPaymentMethodButtonStyles from './AddPaymentMethodButtonStyles';

// #genai Material "payment" (credit card) filled glyph — matches the Figma payment_24px icon.
function CardIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z" />
    </svg>
  );
}
CardIcon.propTypes = { className: PropTypes.string };
CardIcon.defaultProps = { className: '' };

function AddPaymentMethodButton({ id, onSelectBank, onSelectCard }) {
  const { t } = useTranslation();
  const classes = AddPaymentMethodButtonStyles();
  const isMobile = useMediaQuery(640);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const anchorRef = useRef(null);

  const closeMenu = () => setIsMenuOpen(false);
  const toggleMenu = () => setIsMenuOpen((open) => !open);

  // #genai Down/up chevron on both desktop AND mobile (Figma 331:27171 shows the action-down
  // glyph next to "Add Payment Method" on the mobile saved-methods list — earlier mocks omitted it
  // but the latest Phase 2 design keeps the affordance consistent across breakpoints).
  const renderChevron = () => (isMenuOpen ? <EVKeyboardArrowUp /> : <EVKeyboardArrowDown />);

  const handleSelectBank = () => {
    closeMenu();
    onSelectBank();
  };

  const handleSelectCard = () => {
    closeMenu();
    onSelectCard();
  };

  const getOptionElements = () => [`${id}-bank-option`, `${id}-card-option`]
    .map((optionId) => document.getElementById(optionId))
    .filter(Boolean);

  const focusOptionAt = (index) => {
    const options = getOptionElements();
    if (options.length === 0) return;
    options[(index + options.length) % options.length].focus();
  };

  // #genai EVPopper portals the dropdown to the end of <body>, so Tab from the trigger walks the
  // rest of the page instead of entering the menu — leaving the options keyboard-unreachable.
  // Focusing the first option once it mounts fixes that. This is a callback ref rather than an
  // effect because MUI's Portal mounts its children a render after `open` flips, so an effect on
  // isMenuOpen would run while the option button does not exist yet. Mobile uses a modal
  // EVDrawer, which already traps focus, so it is left to MUI.
  const firstOptionRef = useCallback((node) => {
    if (node && isMenuOpen && !isMobile) node.focus();
  }, [isMenuOpen, isMobile]);

  // #genai Keyboard contract for the open menu: Escape closes it and returns focus to the trigger,
  // Up/Down cycle the options. Tab keeps moving through them natively.
  const handleOptionKeyDown = (event) => {
    if (event.key === 'Escape') {
      closeMenu();
      anchorRef.current?.focus();
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const currentIndex = getOptionElements().indexOf(document.activeElement);
    focusOptionAt(event.key === 'ArrowDown' ? currentIndex + 1 : currentIndex - 1);
  };

  // #genai Shared option rows for both the desktop dropdown and the mobile bottom sheet.
  const renderOptions = () => (
    <>
      <button
        type="button"
        id={`${id}-bank-option`}
        ref={firstOptionRef}
        role="menuitem"
        className={`${classes.optionItem} ${classes.optionItemDivider}`}
        onClick={handleSelectBank}
        onKeyDown={handleOptionKeyDown}
      >
        <div className={classes.optionText}>
          <EVTypography className={classes.optionTitle}>
            {t('billing_information__payment_option_bank')}
          </EVTypography>
          <EVTypography className={classes.optionSubtitle}>
            {t('billing_information__payment_option_bank_subtitle')}
          </EVTypography>
        </div>
        <EVAccountBalance className={classes.optionIcon} />
      </button>
      <button
        type="button"
        id={`${id}-card-option`}
        role="menuitem"
        className={classes.optionItem}
        onClick={handleSelectCard}
        onKeyDown={handleOptionKeyDown}
      >
        <EVTypography className={classes.optionTitle}>
          {t('billing_information__payment_option_credit_card')}
        </EVTypography>
        <CardIcon className={classes.optionIcon} />
      </button>
    </>
  );

  return (
    <div className={classes.root}>
      <EVButton
        id={id}
        variant="contained"
        ref={anchorRef}
        className={`${classes.addButton} ${isMenuOpen ? classes.addButtonOpen : ''}`}
        onClick={toggleMenu}
        aria-expanded={isMenuOpen}
        aria-haspopup="menu"
        endIcon={renderChevron()}
      >
        {t('billing_information__add_payment_method')}
      </EVButton>

      {/* ── Desktop: dropdown menu anchored below the button (Figma 630:31658) ── */}
      {!isMobile && (
        <EVPopper
          id={`${id}-popper`}
          open={isMenuOpen}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          className={classes.popper}
        >
          <EVClickAwayListener onClickAway={closeMenu}>
            <EVPaper className={classes.menu} elevation={6} role="menu">
              {renderOptions()}
            </EVPaper>
          </EVClickAwayListener>
        </EVPopper>
      )}

      {/* ── Mobile: bottom-sheet drawer (Figma 305:26485) ── */}
      {isMobile && (
        <EVDrawer
          id={`${id}-drawer`}
          anchor="bottom"
          open={isMenuOpen}
          onClose={closeMenu}
          PaperProps={{ className: classes.drawerPaper }}
          // #genai Figma 305:26527 — 30% black scrim (MUI default is 50%).
          BackdropProps={{ style: { backgroundColor: 'rgba(0,0,0,0.3)' } }}
        >
          <div className={classes.drawerHandleWrap}>
            <div className={classes.drawerHandle} />
          </div>
          <div className={classes.drawerOptions} role="menu">{renderOptions()}</div>
        </EVDrawer>
      )}
    </div>
  );
}

AddPaymentMethodButton.propTypes = {
  id: PropTypes.string,
  onSelectBank: PropTypes.func,
  onSelectCard: PropTypes.func,
};

AddPaymentMethodButton.defaultProps = {
  id: 'add-payment-method-btn',
  // #genai "Bank (preferred)" → dedicated add-ACH page; "Credit Card" → dedicated add-card page.
  onSelectBank: () => history.push(BILLING_INFORMATION_ADD_ACH_ROUTE),
  onSelectCard: () => history.push(BILLING_INFORMATION_ADD_CARD_ROUTE),
};

export default AddPaymentMethodButton;
